import { AttributeCode } from 'services/model/model.service';

export interface MessageRelationship {
  level: string;
  levelKey: string;
}

export type ExtendedAttributeCode = AttributeCode | 'dateRange' | 'messageId' | 'styleId';

export type MessageSentiment = 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';

export interface TradingMessage {
  messageId: number;
  analyserId: string;
  analysisDataId: number;
  currentDataVersion: string;
  level: string;
  levelKey: string;
  type: string;
  message_en: string;
  headline_en: string;
  message_de: string;
  headline_de: string;
  relationships: MessageRelationship[];
  link?: string;
  sentiment: MessageSentiment;
  keyAttributes?: AttributeCode[];
  hidden?: boolean;
  hiddenText?: string;
}

export interface AnalysisData<T> {
  analyserId: string;
  accountNumber: string;
  currentDataVersion: string;
  data: T;
}

export interface TimeSeriesEntry {
  date: string;
  dateValue?: Date;
  value: number;
  expectedValue: number;
  measure: string;
  lowerBound: number;
  upperBound: number;
}

export interface AnalysisEntry {
  attributeValue: string;
  code: string;
  timeSeries: TimeSeriesEntry[];
  children: Record<string, AnalysisEntry[]>;
}

export interface PeriodValue {
  periodCode: string;
  benchmarkGrowth: number;
  tenantGrowth: number;
  benchmarkValue: number;
  tenantValue: number;
  overPerformance: boolean;
  underPerformance: boolean;
  growthDelta: number;
}

export interface MarketshareEventEntry {
  eventType: string;
  itemKey: string;
  combinedPeriodValues: PeriodValue[];
}

export interface MarketshareCategoryEvent {
  key: string;

  parentEvent: MarketshareEventEntry;
  childEvents: MarketshareEventEntry[];
}

export type AnalysisLevel =
  | 'GLOBAL'
  | 'COUNTRY'
  | 'MARKETPLACE'
  | 'CATEGORY'
  | 'BRAND'
  | 'COUNTRY_CHANNEL'
  | 'PRODUCT'
  | 'CHANGE_TYPE'
  | 'PRICE_CHANGE_DATE';

export const ANALYSIS_LEVEL_TO_ATTRIBUTE_CODE: Record<AnalysisLevel, AttributeCode[]> = {
  GLOBAL: [],
  COUNTRY: ['chnCountry'],
  MARKETPLACE: ['chnMarketplace'],
  CATEGORY: ['prdCategory'],
  BRAND: ['prdBrand'],
  COUNTRY_CHANNEL: ['chnCountry', 'chnName'],
  PRODUCT: ['prdNumber'],
  CHANGE_TYPE: [],
  PRICE_CHANGE_DATE: [],
};

export interface PriceChangePrice {
  fromDate: number;
  toDate: number;
  price: number;
  ordQty: number;
  dailyGmv: number;
  dailyCM1?: number;
  totalCM1?: number;
}

export interface PriceChange {
  date: string;
  prdNumber: string;
  artImageUrl?: string;
  prdName?: string;
  styNumber: string;
  chnName: string;
  changeType: 'increase' | 'decrease' | 'promotion';
  fromPrice: PriceChangePrice;
  toPrice: PriceChangePrice;
  promotionPrice?: PriceChangePrice;
  history?: PriceChangeHistory[];
  color?: string;
}

export interface PriceChangeHistory {
  price: number;
  entries: PriceChangeHistoryEntry[];
}

export interface PriceChangeHistoryEntry {
  date: string;
  ordQty: number;
  eans: string[];
}
